/**
 * Takes the consent token out of the URL fragment on `/consent/agent-entered` and scrubs it from
 * the address bar, before telemetry, a bookmark or a screenshot can pick it up.
 *
 * Taking is destructive and React.StrictMode double-invokes state initialisers, so
 * `captureConsentTokenOnce` holds the token for the life of the page load.
 */

import { AGENT_ENTERED_CONSENT_TOKEN_PARAM } from '@shared';

type FragmentLocation = Pick<Location, 'hash' | 'pathname' | 'search'>;
type FragmentHistory = Pick<History, 'replaceState' | 'state'>;

export function takeConsentTokenFromFragment(
  location: FragmentLocation = window.location,
  history: FragmentHistory = window.history,
): string | null {
  const hash = location.hash.startsWith('#') ? location.hash.slice(1) : '';
  if (!hash) return null;

  const params = new URLSearchParams(hash);
  const token = params.get(AGENT_ENTERED_CONSENT_TOKEN_PARAM);
  if (!token) return null;

  params.delete(AGENT_ENTERED_CONSENT_TOKEN_PARAM);
  const remaining = params.toString();
  history.replaceState(
    history.state,
    '',
    `${location.pathname}${location.search}${remaining ? `#${remaining}` : ''}`,
  );

  return token;
}

let capturedToken: string | null | undefined;

/**
 * The token for this page load, read once.
 *
 * A later call that finds a NEW token in the fragment — a second link opened in the same tab —
 * replaces the held one, so the page always acts on the link most recently opened.
 */
export function captureConsentTokenOnce(): string | null {
  const fresh = takeConsentTokenFromFragment();

  if (fresh) {
    capturedToken = fresh;
  } else if (capturedToken === undefined) {
    capturedToken = null;
  }

  return capturedToken;
}

/** Test seam: resets the module-level hold between cases. */
export function resetCapturedConsentTokenForTests(): void {
  capturedToken = undefined;
}
